import { Head, Link, router } from "@inertiajs/react"
import { useState } from "react"

import { HpiFilterSelect } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import { caseSchedulePath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

/* -- Types ------------------------------------------------ */

interface ScheduleEntry {
  id: number
  case_id: number
  case_name: string | null
  day_name: string
  date_desc: string
  time_desc: string
  title: string | null
  notes: string | null
  team: string | null
}

interface EmployeeOption {
  id: number
  name: string
}

interface Props {
  employee: { id: number | null; name: string }
  schedules: ScheduleEntry[]
  can_view_others: boolean
  viewing_other: boolean
  employees?: EmployeeOption[]
}

/* -- Constants -------------------------------------------- */

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Team", href: "/team" },
  { title: "Scheduled Work", href: "/team/schedule" },
]

/* -- Component -------------------------------------------- */

export default function Schedule({
  employee,
  schedules,
  can_view_others,
  viewing_other,
  employees = [],
}: Props) {
  const [selectedEmployee, setSelectedEmployee] = useState(
    employee.id ? String(employee.id) : "",
  )

  function handleEmployeeChange(value: string) {
    setSelectedEmployee(value)
    router.get(
      "/team/schedule",
      { employee_id: value },
      { preserveState: true },
    )
  }

  const pageTitle = viewing_other
    ? `Schedule for ${employee.name}`
    : "Scheduled Work"

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={pageTitle} />

      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">{pageTitle}</h1>

          {can_view_others && employees.length > 0 && (
            <HpiFilterSelect
              label="Employee"
              value={selectedEmployee}
              onChange={handleEmployeeChange}
              options={employees.map((emp) => ({
                value: emp.id,
                label: emp.name,
              }))}
            />
          )}
        </div>

        {viewing_other && (
          <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
            <p className="text-sm font-medium text-gray-700">
              Viewing schedule for{" "}
              <span className="font-semibold">{employee.name}</span>
            </p>
          </div>
        )}

        {schedules.length === 0 ? (
          <div className="rounded border border-gray-200 bg-gray-50 px-6 py-8 text-center">
            <p className="text-hpi-med-gray text-sm">
              No scheduled work found.
            </p>
          </div>
        ) : (
          <div className="border-hpi-light-gray overflow-hidden rounded border bg-white">
            {/* Header */}
            <div className="bg-hpi-pale-blue grid grid-cols-[180px_1fr] border-b px-4 py-2 text-sm font-medium">
              <div>Date</div>
              <div>Description</div>
            </div>

            {/* Rows */}
            <div className="divide-y">
              {schedules.map((entry) => (
                <Link
                  key={entry.id}
                  href={caseSchedulePath(entry.case_id, entry.id)}
                  className="text-hpi-dark-gray hover:bg-hpi-pale-blue grid grid-cols-[180px_1fr] px-4 py-3 transition-colors"
                >
                  <div>
                    {entry.day_name && (
                      <div className="text-hpi-med-gray text-xs">
                        {entry.day_name}
                      </div>
                    )}
                    <div className="text-sm">{entry.date_desc}</div>
                    {entry.time_desc && (
                      <div className="text-hpi-med-gray text-xs">
                        {entry.time_desc}
                      </div>
                    )}
                  </div>
                  <div>
                    {entry.title ? (
                      <>
                        <div className="text-sm">{entry.title}</div>
                        {entry.notes && (
                          <div className="text-hpi-med-gray mt-0.5 text-xs whitespace-pre-wrap">
                            {entry.notes}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-sm">{entry.notes}</div>
                    )}
                    {entry.team && (
                      <div className="text-hpi-med-gray mt-1 text-xs">
                        {entry.team}
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  )
}
